/**
 * Billing Configuration Page - Admin
 * Manage client billing plans and settings
 */

import { useState, useEffect } from 'react';
import {
  DollarSign,
  Settings,
  Users,
  Plus,
  Edit2,
  Save,
  X,
  Check,
  AlertCircle,
  Loader2,
  RefreshCw,
  Mail,
  Calendar,
  FileText,
  Send,
  Eye,
} from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import Input from '../../components/common/Input';
import Toggle from '../../components/common/Toggle';
import { useAllTenants } from '../../hooks/useTenant';
import { useAdminBillingPlans, useAdminSaveBillingPlan } from '../../hooks/useBilling';
import invoiceService from '../../services/invoiceService';
import { useMutation } from '@tanstack/react-query';

// Billing types
const BILLING_TYPES = {
  POST_PAID: 'post_paid',
  TOKEN_BASED: 'token_based',
};

// No fallback data - use API only

function PlanCard({ plan, onEdit, onPreviewInvoice, onGenerateInvoice, isGenerating }) {
  const isPostPaid = plan.billing_type === BILLING_TYPES.POST_PAID;

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h4 className="text-lg font-semibold text-gray-800 dark:text-white">
            {plan.client_id}
          </h4>
          <Badge variant={isPostPaid ? 'purple' : 'blue'}>
            {isPostPaid ? 'Enterprise Post-Paid' : 'Token-Based'}
          </Badge>
        </div>
        <Badge variant={plan.status === 'active' ? 'green' : 'orange'}>
          {plan.status}
        </Badge>
      </div>

      {isPostPaid ? (
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-sm text-gray-500">Per Trip Fee</span>
            <span className="text-sm font-medium">${plan.per_trip_fee}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-gray-500">Revenue Share</span>
            <span className="text-sm font-medium">{plan.revenue_share_percent}%</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-gray-500">Revenue Basis</span>
            <span className="text-sm font-medium capitalize">
              {(plan.revenue_basis || 'gross_sale').replace(/_/g, ' ')}
            </span>
          </div>
          {plan.minimum_bill && (
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">Min Bill</span>
              <span className="text-sm font-medium">${plan.minimum_bill}</span>
            </div>
          )}
          {plan.billing_cap && (
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">Rev Share Cap</span>
              <span className="text-sm font-medium">${plan.billing_cap}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-sm text-gray-500">Payment Terms</span>
            <span className="text-sm font-medium">{plan.payment_terms}</span>
          </div>
          {plan.contract_end && (
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">Contract Ends</span>
              <span className="text-sm font-medium">{plan.contract_end}</span>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-sm text-gray-500">Token Balance</span>
            <span className="text-sm font-medium">{plan.token_balance?.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-gray-500">Monthly Included</span>
            <span className="text-sm font-medium">{plan.tokens_included_monthly?.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-gray-500">Price/Token</span>
            <span className="text-sm font-medium">₹{plan.price_per_token}</span>
          </div>
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 space-y-2">
        <Button variant="outline" size="sm" onClick={() => onEdit(plan)} className="w-full">
          <Edit2 className="w-4 h-4 mr-2" /> Edit Plan
        </Button>

        {/* Invoice Actions - only for post-paid */}
        {isPostPaid && (
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPreviewInvoice(plan.client_id)}
              className="text-xs"
            >
              <Eye className="w-3 h-3 mr-1" /> Preview
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => onGenerateInvoice(plan.client_id)}
              disabled={isGenerating}
              className="text-xs"
            >
              {isGenerating ? (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <Send className="w-3 h-3 mr-1" />
              )}
              Generate
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}

function EditPlanModal({ plan, onSave, onClose }) {
  const [formData, setFormData] = useState({
    client_id: plan?.client_id || '',
    billing_type: plan?.billing_type || BILLING_TYPES.TOKEN_BASED,
    // Post-paid fields
    per_trip_fee: plan?.per_trip_fee || 6.50,
    revenue_share_percent: plan?.revenue_share_percent || 0.25,
    revenue_basis: plan?.revenue_basis || 'gross_sale',  // What counts as revenue
    minimum_bill: plan?.minimum_bill || null,             // Min monthly bill
    billing_cap: plan?.billing_cap || null,               // Max revenue share
    // Token-based fields
    token_balance: plan?.token_balance || 0,
    tokens_included_monthly: plan?.tokens_included_monthly || 25000,
    price_per_token: plan?.price_per_token || 5.50,
    // Common fields
    currency: plan?.currency || 'USD',
    payment_terms: plan?.payment_terms || 'net_30',
    status: plan?.status || 'active',
    contract_start: plan?.contract_start || '',
    contract_end: plan?.contract_end || '',
    auto_renew: plan?.auto_renew ?? true,
    // Invoice configuration
    invoice_email: plan?.invoice_email || '',
    invoice_cc: plan?.invoice_cc?.join(', ') || '',
    invoice_schedule: plan?.invoice_schedule || 'monthly',
    invoice_day: plan?.invoice_day || 1,
    invoice_enabled: plan?.invoice_enabled ?? true,
    company_name: plan?.company_name || '',
    company_address: plan?.company_address || '',
  });

  const isPostPaid = formData.billing_type === BILLING_TYPES.POST_PAID;
  const update = (key, value) => setFormData(prev => ({ ...prev, [key]: value }));

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
            {plan ? `Edit Plan: ${plan.client_id}` : 'Create Billing Plan'}
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Client ID (only for new plans) */}
          {!plan && (
            <Input
              label="Client ID"
              value={formData.client_id}
              onChange={(e) => update('client_id', e.target.value)}
              placeholder="e.g., acme-corp"
            />
          )}

          {/* Billing Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Billing Type
            </label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  checked={formData.billing_type === BILLING_TYPES.TOKEN_BASED}
                  onChange={() => update('billing_type', BILLING_TYPES.TOKEN_BASED)}
                  className="mr-2"
                />
                Token-Based
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  checked={formData.billing_type === BILLING_TYPES.POST_PAID}
                  onChange={() => update('billing_type', BILLING_TYPES.POST_PAID)}
                  className="mr-2"
                />
                Post-Paid (Enterprise)
              </label>
            </div>
          </div>

          {/* Post-paid settings */}
          {isPostPaid && (
            <>
              <Input
                label="Per Trip Fee (USD)"
                type="number"
                step="0.01"
                value={formData.per_trip_fee}
                onChange={(e) => update('per_trip_fee', parseFloat(e.target.value))}
              />
              <Input
                label="Revenue Share (%)"
                type="number"
                step="0.01"
                value={formData.revenue_share_percent}
                onChange={(e) => update('revenue_share_percent', parseFloat(e.target.value))}
              />

              {/* Revenue Basis - What counts as revenue */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Revenue Basis (what to include in revenue share)
                </label>
                <select
                  value={formData.revenue_basis || 'gross_sale'}
                  onChange={(e) => update('revenue_basis', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="gross_sale">Gross Sale (ticket + all fees)</option>
                  <option value="net_sale">Net Sale (after discounts/refunds)</option>
                  <option value="ticket_price">Ticket Price Only (no fees)</option>
                  <option value="gross_minus_fees">Gross - Booking/Facility Fees</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Determines which revenue figure from order data is used for revenue share calculation
                </p>
              </div>

              {/* Minimum Bill */}
              <Input
                label="Minimum Monthly Bill (USD) - Optional"
                type="number"
                step="0.01"
                placeholder="e.g., 500.00"
                value={formData.minimum_bill || ''}
                onChange={(e) => update('minimum_bill', e.target.value ? parseFloat(e.target.value) : null)}
              />

              {/* Billing Cap */}
              <Input
                label="Revenue Share Cap (USD/month) - Optional"
                type="number"
                step="0.01"
                placeholder="e.g., 10000.00"
                value={formData.billing_cap || ''}
                onChange={(e) => update('billing_cap', e.target.value ? parseFloat(e.target.value) : null)}
              />

              <Input
                label="Contract Start"
                type="date"
                value={formData.contract_start}
                onChange={(e) => update('contract_start', e.target.value)}
              />
              <Input
                label="Contract End"
                type="date"
                value={formData.contract_end}
                onChange={(e) => update('contract_end', e.target.value)}
              />
            </>
          )}

          {/* Token-based settings */}
          {!isPostPaid && (
            <>
              <Input
                label="Initial Token Balance"
                type="number"
                value={formData.token_balance}
                onChange={(e) => update('token_balance', parseInt(e.target.value))}
              />
              <Input
                label="Monthly Included Tokens"
                type="number"
                value={formData.tokens_included_monthly}
                onChange={(e) => update('tokens_included_monthly', parseInt(e.target.value))}
              />
              <Input
                label="Price Per Token (INR)"
                type="number"
                step="0.01"
                value={formData.price_per_token}
                onChange={(e) => update('price_per_token', parseFloat(e.target.value))}
              />
            </>
          )}

          {/* Payment Terms */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Payment Terms
            </label>
            <select
              value={formData.payment_terms}
              onChange={(e) => update('payment_terms', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
            >
              <option value="due_on_receipt">Due on Receipt</option>
              <option value="net_15">Net 15</option>
              <option value="net_30">Net 30</option>
              <option value="net_60">Net 60</option>
            </select>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Status
            </label>
            <select
              value={formData.status}
              onChange={(e) => update('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
            >
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* Auto Renew */}
          <Toggle
            label="Auto Renew"
            enabled={formData.auto_renew}
            onChange={(v) => update('auto_renew', v)}
          />

          {/* Invoice Configuration Section */}
          <div className="pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
              <Mail className="w-4 h-4" /> Invoice Settings
            </h4>

            <div className="space-y-4">
              <Input
                label="Company Name (for invoice)"
                value={formData.company_name}
                onChange={(e) => update('company_name', e.target.value)}
                placeholder="e.g., OurBus Inc."
              />

              <Input
                label="Company Address"
                value={formData.company_address}
                onChange={(e) => update('company_address', e.target.value)}
                placeholder="e.g., 123 Main St, New York, NY"
              />

              <Input
                label="Invoice Email"
                type="email"
                value={formData.invoice_email}
                onChange={(e) => update('invoice_email', e.target.value)}
                placeholder="billing@company.com"
              />

              <Input
                label="CC Emails (comma separated)"
                value={formData.invoice_cc}
                onChange={(e) => update('invoice_cc', e.target.value)}
                placeholder="finance@company.com, accounts@company.com"
              />

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Invoice Schedule
                  </label>
                  <select
                    value={formData.invoice_schedule}
                    onChange={(e) => update('invoice_schedule', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="weekly">Weekly</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>

                <Input
                  label={formData.invoice_schedule === 'weekly' ? 'Day of Week (0-6)' : 'Day of Month (1-28)'}
                  type="number"
                  min={formData.invoice_schedule === 'weekly' ? 0 : 1}
                  max={formData.invoice_schedule === 'weekly' ? 6 : 28}
                  value={formData.invoice_day}
                  onChange={(e) => update('invoice_day', parseInt(e.target.value) || 1)}
                />
              </div>

              <Toggle
                label="Auto-generate Invoices"
                enabled={formData.invoice_enabled}
                onChange={(v) => update('invoice_enabled', v)}
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => onSave({ ...plan, ...formData })}
            className="flex-1"
          >
            <Save className="w-4 h-4 mr-2" /> Save Plan
          </Button>
        </div>
      </div>
    </div>
  );
}

// Invoice Preview Modal
function InvoicePreviewModal({ isOpen, onClose, clientId, previewData, isLoading }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-500" />
            Invoice Preview: {clientId}
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            <span className="ml-2 text-gray-500">Loading preview...</span>
          </div>
        ) : previewData ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-sm text-gray-500 dark:text-gray-400">Billing Period</p>
                <p className="font-semibold text-gray-800 dark:text-white">{previewData.billing_period}</p>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-sm text-gray-500 dark:text-gray-400">Total Amount</p>
                <p className="font-semibold text-2xl text-gray-800 dark:text-white">
                  ${previewData.total?.toLocaleString() || '0.00'}
                </p>
              </div>
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-3">Charges Breakdown</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Trips ({previewData.trips_count || 0})</span>
                  <span className="font-medium">${previewData.trip_charges?.toLocaleString() || '0.00'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Revenue Share</span>
                  <span className="font-medium">${previewData.revenue_share_charges?.toLocaleString() || '0.00'}</span>
                </div>
                <div className="flex justify-between border-t border-gray-200 dark:border-gray-700 pt-2 mt-2">
                  <span className="font-semibold text-gray-800 dark:text-white">Total</span>
                  <span className="font-semibold text-lg">${previewData.total?.toLocaleString() || '0.00'}</span>
                </div>
              </div>
            </div>

            {previewData.gross_revenue > 0 && (
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-sm text-blue-600 dark:text-blue-400">
                  Based on ${previewData.gross_revenue?.toLocaleString()} gross revenue
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            No preview data available
          </div>
        )}

        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button variant="outline" onClick={onClose} className="w-full">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function BillingConfiguration() {
  // Fetch billing plans from API
  const {
    data: apiPlansData,
    isLoading,
    isError,
    error,
    refetch
  } = useAdminBillingPlans();

  // Mutation for saving plans
  const savePlanMutation = useAdminSaveBillingPlan();

  // Invoice mutations
  const previewInvoiceMutation = useMutation({
    mutationFn: ({ clientId, month }) => invoiceService.adminPreviewInvoice(clientId, month),
  });

  const generateInvoiceMutation = useMutation({
    mutationFn: ({ clientId, month, sendEmail }) => invoiceService.adminGenerateInvoice(clientId, month, sendEmail),
  });

  const [editingPlan, setEditingPlan] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [previewClientId, setPreviewClientId] = useState(null);
  const [generatingClientId, setGeneratingClientId] = useState(null);

  // Use API data only - no fallback
  const plans = apiPlansData?.plans || [];
  const postPaidPlans = plans.filter(p => p.billing_type === BILLING_TYPES.POST_PAID);
  const tokenPlans = plans.filter(p => p.billing_type === BILLING_TYPES.TOKEN_BASED);

  const handleSave = async (planData) => {
    setSaveError(null);
    setSaveSuccess(false);

    try {
      await savePlanMutation.mutateAsync({
        clientId: planData.client_id,
        planData: {
          billing_type: planData.billing_type,
          // Post-paid fields
          per_trip_fee: planData.per_trip_fee,
          revenue_share_percent: planData.revenue_share_percent,
          revenue_basis: planData.revenue_basis || 'gross_sale',
          minimum_bill: planData.minimum_bill || null,
          billing_cap: planData.billing_cap || null,
          // Token-based fields
          token_balance: planData.token_balance,
          tokens_included_monthly: planData.tokens_included_monthly,
          price_per_token: planData.price_per_token,
          // Common fields
          currency: planData.currency,
          billing_cycle: planData.billing_cycle || 'monthly',
          payment_terms: planData.payment_terms,
          status: planData.status,
          auto_renew: planData.auto_renew,
          contract_start: planData.contract_start,
          contract_end: planData.contract_end,
          // Invoice configuration
          company_name: planData.company_name,
          company_address: planData.company_address,
          invoice_email: planData.invoice_email,
          invoice_cc: planData.invoice_cc ? planData.invoice_cc.split(',').map(e => e.trim()).filter(Boolean) : [],
          invoice_schedule: planData.invoice_schedule,
          invoice_day: planData.invoice_day,
          invoice_enabled: planData.invoice_enabled,
        }
      });
      setSaveSuccess(true);
      setEditingPlan(null);
      setShowCreateModal(false);
      // Refetch to get updated data
      refetch();
    } catch (err) {
      console.error('Failed to save billing plan:', err);
      setSaveError(err.message || 'Failed to save billing plan');
    }
  };

  // Handle invoice preview
  const handlePreviewInvoice = async (clientId) => {
    const month = new Date().toISOString().slice(0, 7); // Current month YYYY-MM
    setPreviewClientId(clientId);
    previewInvoiceMutation.mutate({ clientId, month });
  };

  // Handle invoice generation
  const handleGenerateInvoice = async (clientId) => {
    const month = new Date().toISOString().slice(0, 7);
    setGeneratingClientId(clientId);
    try {
      await generateInvoiceMutation.mutateAsync({ clientId, month, sendEmail: true });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(err.message || 'Failed to generate invoice');
    } finally {
      setGeneratingClientId(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Billing Configuration</h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Manage billing plans for all clients
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => refetch()} disabled={isLoading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="primary" onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-2" /> New Plan
          </Button>
        </div>
      </div>

      {/* Status Messages */}
      {isError && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <p className="text-yellow-800 dark:text-yellow-200 text-sm">
            <AlertCircle className="w-4 h-4 inline mr-2" />
            Unable to load billing plans from API. Showing cached data.
            <span className="block mt-1 text-xs opacity-75">{error?.message}</span>
          </p>
        </div>
      )}

      {saveError && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200 text-sm">
            <AlertCircle className="w-4 h-4 inline mr-2" />
            {saveError}
          </p>
        </div>
      )}

      {saveSuccess && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <p className="text-green-800 dark:text-green-200 text-sm">
            <Check className="w-4 h-4 inline mr-2" />
            Billing plan saved successfully!
          </p>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          <span className="ml-2 text-gray-500">Loading billing plans...</span>
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
              <DollarSign className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Enterprise Clients</p>
              <p className="text-2xl font-bold">{postPaidPlans.length}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Token Clients</p>
              <p className="text-2xl font-bold">{tokenPlans.length}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-xl">
              <Check className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Active Plans</p>
              <p className="text-2xl font-bold">{plans.filter(p => p.status === 'active').length}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-xl">
              <Settings className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Plans</p>
              <p className="text-2xl font-bold">{plans.length}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Enterprise (Post-Paid) Plans */}
      {postPaidPlans.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-purple-600" />
            Enterprise Plans (Post-Paid)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {postPaidPlans.map(plan => (
              <PlanCard
                key={plan.client_id}
                plan={plan}
                onEdit={setEditingPlan}
                onPreviewInvoice={handlePreviewInvoice}
                onGenerateInvoice={handleGenerateInvoice}
                isGenerating={generatingClientId === plan.client_id}
              />
            ))}
          </div>
        </div>
      )}

      {/* Token-Based Plans */}
      {tokenPlans.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            Token-Based Plans
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tokenPlans.map(plan => (
              <PlanCard
                key={plan.client_id}
                plan={plan}
                onEdit={setEditingPlan}
                onPreviewInvoice={handlePreviewInvoice}
                onGenerateInvoice={handleGenerateInvoice}
                isGenerating={generatingClientId === plan.client_id}
              />
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {plans.length === 0 && (
        <Card className="text-center py-12">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-2">
            No billing plans configured
          </h3>
          <p className="text-gray-500 mb-4">Get started by creating your first billing plan</p>
          <Button variant="primary" onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-2" /> Create Plan
          </Button>
        </Card>
      )}

      {/* Edit Modal */}
      {editingPlan && (
        <EditPlanModal
          plan={editingPlan}
          onSave={handleSave}
          onClose={() => setEditingPlan(null)}
        />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <EditPlanModal
          plan={null}
          onSave={handleSave}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {/* Invoice Preview Modal */}
      <InvoicePreviewModal
        isOpen={!!previewClientId}
        onClose={() => setPreviewClientId(null)}
        clientId={previewClientId}
        previewData={previewInvoiceMutation.data}
        isLoading={previewInvoiceMutation.isPending}
      />
    </div>
  );
}

